// @givmo/app-admin — Company Fundraisers decode/display helpers (EP-WEB3).
//
// Pure, side-effect-free helpers for the company-owned fundraiser surface — a company creating and
// managing a fundraiser it OWNS (backend `POST/GET /admin/company/fundraisers`, delegating to the
// unmodified fundraiser lifecycle; the acting admin is the `creator_user_id`/OWNER of record and the
// fundraiser carries an `owner_company` byline). Kept as their own no-React module so the list decode,
// the status mapping, the money formatting, and the cover-file validation are unit-testable in
// isolation (matching the `give-decode.ts` / `employees-decode.ts` split). FCM-WEB18 adds the honest
// status badges, the reading of the server's `lifecycle` hints and the lifecycle copy.
//
// Tolerant reader: every list decode goes through the shared `extractList`, reading BOTH the bare row
// array AND a `{ data: [...] }` envelope, degrading to `[]` rather than throwing. That matters for the
// picker searches (`GET /charities|/etfs|/charity-collections`): those DO return the paginated envelope,
// but the api-client response middleware unwraps it to the bare array before the query sees it — so the
// generated `XxxPaginatedResponse` type is a compile-time fiction at runtime. Money: `FundraiserOut`
// serializes `goal_amount` and `stats.total_raised` as NUMBERS (dollars) on the wire — already numeric,
// so these helpers only FORMAT for display; they never parse a money STRING to a float or re-round.

import { extractList } from '@givmo/api-client/list';
import { firstErrorCode, isGivmoError } from '@givmo/api-client';
import type { FundraiserOut, FundraiserStatus, FundraiserUpdate } from '@givmo/api-client';

// ── List decode ────────────────────────────────────────────────────────────────────────────────

/**
 * Tolerant decode of `GET /admin/company/fundraisers`. The route returns a BARE `List[FundraiserOut]`;
 * `extractList` also tolerates a `{ data: [...] }` envelope and yields `[]` on an unexpected shape —
 * never throws.
 */
export function extractCompanyFundraisers(body: unknown): FundraiserOut[] {
  return extractList<FundraiserOut>(body);
}

// ── Status and the server's lifecycle hints (FCM-WEB18, plan §FC-15) ─────────────────────────

export interface FundraiserStatusView {
  label: string;
  tone: 'success' | 'neutral' | 'warning' | 'danger';
}

/** Closed is Active with `closed_at` set: the status stays Active on the wire (D-CAMP-2). */
export function isClosed(f: FundraiserOut): boolean {
  return f.status === 'Active' && typeof f.closed_at === 'string' && f.closed_at.length > 0;
}

/**
 * The server's `lifecycle` hints (FCM-BE15; the flags' arms FCM-BE14/BE16/BE21): which lifecycle
 * action the server would accept from THIS admin on THIS fundraiser. The private read and every
 * company-list row carry them. Tolerant: a missing or malformed object (an older backend) is `null`,
 * and each flag is true only when the server sent `true`, so an absent `takedown_locked` (a backend
 * without FCM-BE21) reads false.
 */
export interface FundraiserLifecycleView {
  canClose: boolean;
  canReopen: boolean;
  canDeactivate: boolean;
  canReactivate: boolean;
  canDelete: boolean;
  removedByGivmo: boolean;
  takedownLocked: boolean;
}

export function fundraiserLifecycle(f: FundraiserOut): FundraiserLifecycleView | null {
  const raw: unknown = f.lifecycle;
  if (raw === null || typeof raw !== 'object') return null;
  const hints = raw as Record<string, unknown>;
  const flag = (key: string) => hints[key] === true;
  return {
    canClose: flag('can_close'),
    canReopen: flag('can_reopen'),
    canDeactivate: flag('can_deactivate'),
    canReactivate: flag('can_reactivate'),
    canDelete: flag('can_delete'),
    removedByGivmo: flag('removed_by_givmo'),
    takedownLocked: flag('takedown_locked'),
  };
}

/**
 * The honest status badge (Jonathan's rulings Q3 and Q6): Draft amber, Active green, Closed (Active +
 * `closed_at`) grey, the company's own Deactivated grey, and a Givmo takedown (`removed_by_givmo`)
 * "Taken down" in red, the one ending the company did not choose. An unknown value degrades to a
 * neutral chip showing the raw string (tolerant reader: an added backend status never crashes a row).
 */
export function fundraiserStatusView(f: FundraiserOut): FundraiserStatusView {
  if (isClosed(f)) return { label: 'Closed', tone: 'neutral' };
  const status: FundraiserStatus | string | null | undefined = f.status;
  switch (status) {
    case 'Active':
      return { label: 'Active', tone: 'success' };
    case 'Draft':
      return { label: 'Draft', tone: 'warning' };
    case 'Deactivated':
      return fundraiserLifecycle(f)?.removedByGivmo
        ? { label: 'Taken down', tone: 'danger' }
        : { label: 'Deactivated', tone: 'neutral' };
    default:
      return {
        label: typeof status === 'string' && status.trim().length > 0 ? status : 'Unknown',
        tone: 'neutral',
      };
  }
}

/** What the manage page offers. Each action shows exactly when the server's hint says so. */
export interface LifecycleActions {
  close: boolean;
  reopen: boolean;
  deactivate: boolean;
  reactivate: boolean;
  /** Delete draft: `can_delete` on a Draft (the DELETE hard-deletes it). */
  deleteDraft: boolean;
  /** Delete forever: `can_delete` on a Deactivated row, unfunded (ruling Q9). */
  deleteForever: boolean;
  /** "Need it taken down?": `takedown_locked` (FCM-BE21), never inferred from `can_deactivate`. */
  contactSupport: boolean;
  removedByGivmo: boolean;
}

export function lifecycleActions(f: FundraiserOut): LifecycleActions {
  const hints = fundraiserLifecycle(f);
  if (hints === null) {
    // An older backend sends no hints: today's local rule, minus the mislabel (the DELETE deactivates
    // an open row and hard-deletes a draft). Nothing a backend without the hints cannot serve.
    return {
      close: false,
      reopen: false,
      deactivate: f.status === 'Active' && !isClosed(f),
      reactivate: false,
      deleteDraft: f.status === 'Draft',
      deleteForever: false,
      contactSupport: false,
      removedByGivmo: false,
    };
  }
  return {
    close: hints.canClose,
    reopen: hints.canReopen,
    deactivate: hints.canDeactivate,
    reactivate: hints.canReactivate,
    deleteDraft: hints.canDelete && f.status === 'Draft',
    deleteForever: hints.canDelete && f.status === 'Deactivated',
    contactSupport: hints.takedownLocked,
    removedByGivmo: hints.removedByGivmo,
  };
}

/**
 * The server freezes a fundraiser's content while `closed_at` stands, whatever its status: a closed
 * page, and one a Close froze before it was deactivated (FCM-WEB28; the server's `content_writable`).
 * Reactivating such a page returns it to Closed, and only Reopen clears the freeze.
 */
export function isFrozenByClose(f: FundraiserOut): boolean {
  return typeof f.closed_at === 'string' && f.closed_at.length > 0;
}

/** A page is a read-only record while a Close freezes it or Givmo took it down (rulings Q5 and Q7). */
export function isReadOnlyRecord(f: FundraiserOut): boolean {
  return isFrozenByClose(f) || lifecycleActions(f).removedByGivmo;
}

export function isPublished(f: FundraiserOut): boolean {
  return f.status === 'Active';
}

export function isDraft(f: FundraiserOut): boolean {
  return f.status === 'Draft';
}

// ── Lifecycle copy (plan §FC-15 "Copy", as Jonathan ruled it on 2026-09-27) ─────────────────────

/** Every support surface names this address (house rule; the plan's Contact support lines). */
export const SUPPORT_EMAIL = 'support@givmocharitable.org';

function supportMailto(subject: string): string {
  return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}`;
}

/** The funded card's Contact support mailto: subject "Take down: {name} ({id})" (ruling Q4). */
export function takedownSupportHref(f: FundraiserOut): string {
  return supportMailto(`Take down: ${f.name ?? 'fundraiser'} (${f.id})`);
}

/** The takedown banner's Contact support mailto: the organizer asks about a takedown Givmo already
 *  made, so the subject says so, "Question about a takedown: {name} ({id})" (Jonathan's app mockup
 *  ruling A8, 2026-09-27), and support never reads it as a new takedown request. */
export function takedownQuestionHref(f: FundraiserOut): string {
  return supportMailto(`Question about a takedown: ${f.name ?? 'fundraiser'} (${f.id})`);
}

/** Whether the public page shows the raised total (`show_statistics`); only `false` hides it. */
function totalsShown(f: FundraiserOut): boolean {
  return f.show_statistics !== false;
}

function hasGoal(f: FundraiserOut): boolean {
  return typeof f.goal_amount === 'number' || typeof f.goal_progress_percent === 'number';
}

/** The closed banner says what the closed page shows (ruling A3): the final total, or with totals
 *  hidden its final goal progress, or nothing more when there is no goal. */
export function closedBannerCopy(f: FundraiserOut): string {
  if (totalsShown(f))
    return 'This fundraiser is closed. New donations are off, and its page shows the final total.';
  return hasGoal(f)
    ? 'This fundraiser is closed. New donations are off, and its page shows its final goal progress.'
    : 'This fundraiser is closed. New donations are off.';
}

/** The Close dialog's body, by the same rule (ruling A3). */
export function closeDialogCopy(f: FundraiserOut): string {
  return totalsShown(f)
    ? 'Closing stops all new donations. The page keeps showing its final total, and you can reopen it at any time.'
    : 'Closing stops all new donations. The page stays up, and you can reopen it at any time.';
}

export const LIFECYCLE_COPY = {
  closeTitle: 'Close this fundraiser?',
  closeConfirm: 'Close fundraiser',
  reopenTitle: 'Reopen this fundraiser?',
  reopenBody: 'This fundraiser will accept donations again.',
  reopenConfirm: 'Reopen',
  deactivateCardTitle: 'Deactivate fundraiser',
  deactivateCardBody: 'Hides the fundraiser from donors. You can reactivate it later.',
  deactivateTitle: 'Deactivate this fundraiser?',
  deactivateBody:
    'Deactivating hides this fundraiser’s page and stops new donations. Donations already made are unaffected. You can reactivate it later.',
  deactivateConfirm: 'Deactivate',
  deleteDraftCardTitle: 'Delete draft',
  deleteDraftCardBody: 'Deletes this draft for good.',
  deleteDraftTitle: 'Delete this draft?',
  deleteDraftConfirm: 'Delete draft',
  deleteForeverCardTitle: 'Delete forever',
  deleteForeverCardBody: 'Deletes this fundraiser for good.',
  deleteForeverTitle: 'Delete this fundraiser?',
  deleteForeverConfirm: 'Delete fundraiser',
  cantBeUndone: 'This can’t be undone.',
  deactivatedBanner: 'This fundraiser is deactivated, so its page is hidden.',
  takedownLead: 'Givmo took this fundraiser down.',
  takedownLink: 'Contact support',
  takedownTail: 'with questions.',
  fundedTitle: 'Need it taken down?',
  fundedBody: 'Once a fundraiser has donations, only Givmo can take it down.',
  fundedAction: 'Contact support',
  closedDetails: 'Reopen the fundraiser to edit its name, description, or goal.',
  takenDownDetails: 'This fundraiser can’t be edited while it’s taken down.',
  closedRecipients: 'Reopen the fundraiser to change its recipients.',
  /** A page a Close froze before it was deactivated (FCM-WEB28): Reactivate returns it to Closed,
   *  and only Reopen clears the freeze. Jonathan approved both 2026-10-06, verbatim here and in the
   *  Fundraisers module. */
  frozenDeactivatedDetails:
    'Reactivate and then reopen the fundraiser to edit its name, description, or goal.',
  frozenDeactivatedRecipients:
    'Reactivate and then reopen the fundraiser to change its recipients.',
  noCover: 'No cover image.',
} as const;

export const LIFECYCLE_TOASTS = {
  closed: 'Fundraiser closed.',
  reopened: 'Fundraiser reopened.',
  deactivated: 'Fundraiser deactivated.',
  reactivated: 'Fundraiser reactivated.',
  draftDeleted: 'Draft deleted.',
  deleted: 'Fundraiser deleted.',
} as const;

/**
 * A write refused because the page went stale: Givmo took the fundraiser down (FCM-BE27/BE16, 409
 * `FUNDRAISER_REMOVED_BY_GIVMO`), or it gained a donation and only Givmo can take it down now
 * (FCM-BE16, 409 `FUNDRAISER_HAS_DONATIONS`). Keyed on the typed code alone, never the English; the
 * message is the plan's refusal copy, which is the server's own detail. `null` for anything else.
 */
export function staleLifecycleRefusal(error: unknown): string | null {
  if (!isGivmoError(error)) return null;
  switch (firstErrorCode(error)) {
    case 'FUNDRAISER_REMOVED_BY_GIVMO':
      return `Givmo took this fundraiser down. Email ${SUPPORT_EMAIL} with questions.`;
    case 'FUNDRAISER_HAS_DONATIONS':
      return `This fundraiser has donations, so it can’t be deactivated. You can close it instead. If it needs to come down, email ${SUPPORT_EMAIL}.`;
    default:
      return null;
  }
}

/**
 * Whether the server refused a write with a 409: the row moved under the page (Givmo took it down,
 * it was closed or reopened elsewhere, a donation landed). The page re-reads on every one of them so
 * it shows what the server holds; the message stays keyed on the typed codes above, and otherwise
 * keeps the server's own words.
 */
export function isConflictRefusal(error: unknown): boolean {
  return isGivmoError(error) && error.status === 409;
}

// ── Money / stats (display-only; wire values are already numbers) ─────────────────────────────────

// Whole dollars read bare; an amount with cents always shows both cent digits — "$1,234.5" is not how
// money is written.
const USD_WHOLE = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});
const USD_CENTS = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Format a wire dollar NUMBER for display (e.g. 1234.5 → "$1,234.50", 500 → "$500"). A null / non-finite
 *  value renders "$0". Display-only — never re-rounds a money string (the wire value is already numeric). */
export function formatFundraiserAmount(amount: number | null | undefined): string {
  if (typeof amount !== 'number' || !Number.isFinite(amount)) return '$0';
  return (Number.isInteger(amount) ? USD_WHOLE : USD_CENTS).format(amount);
}

/** Missing statistics are uncomputed, not a computed zero. */
export function fundraiserRaised(f: FundraiserOut): string | null {
  const amount = f.stats?.total_raised;
  return typeof amount === 'number' && Number.isFinite(amount)
    ? formatFundraiserAmount(amount)
    : null;
}

/** The formatted goal, or null when no goal is set (an optional field). */
export function fundraiserGoal(f: FundraiserOut): string | null {
  return typeof f.goal_amount === 'number' ? formatFundraiserAmount(f.goal_amount) : null;
}

export function fundraiserDonationCount(f: FundraiserOut): number {
  const c = f.stats?.donation_count;
  return typeof c === 'number' && Number.isFinite(c) ? c : 0;
}

/** Display-only count. Keep the existing numeric helper's lifecycle semantics. */
export function fundraiserDonationCountForDisplay(f: FundraiserOut): number | null {
  const count = f.stats?.donation_count;
  return typeof count === 'number' && Number.isFinite(count) ? count : null;
}

/** Goal progress as an integer percent clamped to [0, 100], or null when the wire omits it / has no goal. */
export function fundraiserGoalPercent(f: FundraiserOut): number | null {
  const p = f.goal_progress_percent;
  if (typeof p !== 'number' || !Number.isFinite(p)) return null;
  return Math.max(0, Math.min(100, Math.round(p)));
}

// ── Recipient items ──────────────────────────────────────────────────────────────────────────────

export {
  currentRecipients,
  extractCharityResults,
  extractCollectionResults,
  extractEtfResults,
} from '@givmo/fundraisers-module';
export type {
  RecipientKind,
  RecipientItem,
  RecipientSearchResult,
} from '@givmo/fundraisers-module';
import { currentRecipients } from '@givmo/fundraisers-module';

export function fundraiserItemCount(f: FundraiserOut): number {
  return currentRecipients(f).length;
}

/** Whether the recipient-item list is FROZEN. The backend freezes the item list once the first donation
 *  lands; we soft-disable add/remove when `donation_count > 0` (the backend is authoritative and returns
 *  a clean error if a stale UI still tries — that error is surfaced regardless). */
export function itemsFrozen(f: FundraiserOut): boolean {
  return fundraiserDonationCount(f) > 0;
}

// ── Picker search decode (charities / ETFs / collections) ─────────────────────────────────────────

// ── Create/edit field constraints + goal parsing ─────────────────────────────────────────────────

/** Backend field caps (mirrored for fast client feedback; the backend is authoritative). */
export const NAME_MAX_LENGTH = 100;
export const DESCRIPTION_MAX_LENGTH = 2000;

export type GoalParse = { value: string | null } | { error: string };

/**
 * Parse the goal-amount input. Empty → `{ value: null }` (goal is OPTIONAL). A valid positive dollar
 * amount (whole or up to 2 decimals) → `{ value: <normalized decimal STRING> }` — sent to the wire AS A
 * STRING (goal_amount accepts `number | string`) so no float rounding is introduced. Anything else →
 * `{ error }`.
 */
export function parseGoalDollars(raw: string): GoalParse {
  const t = raw.trim();
  if (t === '') return { value: null };
  if (!/^\d+(\.\d{1,2})?$/.test(t)) {
    return { error: 'Enter a dollar amount like 500 or 500.00.' };
  }
  const n = Number(t);
  if (!Number.isFinite(n) || n <= 0) return { error: 'Goal must be greater than $0.' };
  return { value: t };
}

/**
 * The goal half of a `PUT /fundraisers/{id}` body. That route reads a null or absent `goal_amount` as
 * "unchanged", so an emptied field removes a SAVED goal with `remove_goal: true` (FCM-BE13), and an
 * emptied field over no saved goal sends neither key. The two keys never travel together: the server
 * refuses that pair with a 422.
 */
export function goalUpdate(
  goal: string | null,
  saved: FundraiserOut['goal_amount'],
): Pick<FundraiserUpdate, 'goal_amount' | 'remove_goal'> {
  if (goal !== null) return { goal_amount: goal };
  return saved === null || saved === undefined ? {} : { remove_goal: true };
}

export {
  COVER_ACCEPT,
  COVER_MAX_BYTES,
  COVER_TOO_LARGE_MESSAGE,
  COVER_WRONG_TYPE_MESSAGE,
  isAcceptableCover,
} from '@givmo/fundraisers-module';
